/**
 * Formato de cifras y fechas.
 *
 * Todo el sistema muestra pesos colombianos y fechas de Bogotá. Se centraliza
 * aquí porque dos pantallas que formatean distinto la misma cifra hacen dudar
 * de las dos.
 */

const ZONA_HORARIA = 'America/Bogota'
const LOCAL = 'es-CO'

const formateadorMoneda = new Intl.NumberFormat(LOCAL, {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

const formateadorFecha = new Intl.DateTimeFormat(LOCAL, {
  timeZone: ZONA_HORARIA,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
})

/**
 * El de las fechas SIN HORA, que es un problema distinto.
 *
 * Una columna DATE —«2026-08-25»— no es un instante: es un día del calendario
 * y no le corresponde ninguna hora ni ninguna zona. Pasarla por el formateador
 * de arriba la convierte en un instante y la mueve.
 *
 * Lo que pasaba: `new Date(2026, 7, 25)` crea la medianoche EN LA ZONA DEL
 * NAVEGADOR, y luego se formateaba en la de Bogotá. En un equipo puesto en
 * Colombia coincide y no se nota; en uno puesto en UTC —o en el teléfono de
 * alguien de viaje— esa medianoche cae cinco horas antes y la fecha retrocede
 * un día. Las cotizaciones de hoy aparecían fechadas ayer, que es exactamente
 * lo que hace pensar que no están llegando.
 *
 * Se arma en UTC y se lee en UTC: así los números que entran son los mismos
 * que salen, esté donde esté quien mira.
 */
const formateadorDia = new Intl.DateTimeFormat(LOCAL, {
  timeZone: 'UTC',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
})

const formateadorFechaHora = new Intl.DateTimeFormat(LOCAL, {
  timeZone: ZONA_HORARIA,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

/** Pesos sin decimales: en obra nadie cotiza centavos. */
export function pesos(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return '—'
  return formateadorMoneda.format(valor)
}

/** Fecha corta: 14 ago 2026. */
export function fecha(valor: string | null | undefined): string {
  if (!valor) return '—'

  // Las columnas DATE llegan como '2026-08-14'. No son un instante sino un día
  // del calendario, así que se arman y se leen en UTC: ni se interpretan como
  // hora local ni se traducen a otra zona. Ver formateadorDia.
  if (/^\d{4}-\d{2}-\d{2}$/.test(valor)) {
    const [anio, mes, dia] = valor.split('-').map(Number)
    return formateadorDia.format(new Date(Date.UTC(anio, mes - 1, dia)))
  }

  return formateadorFecha.format(new Date(valor))
}

/** Fecha con hora: 14 ago 2026, 3:20 p. m. */
export function fechaHora(valor: string | null | undefined): string {
  if (!valor) return '—'
  return formateadorFechaHora.format(new Date(valor))
}

/**
 * Horas de trabajo. Se muestran con decimal solo cuando lo tienen: "8 h" se
 * lee mejor que "8,00 h", y "7,5 h" no se puede redondear sin mentir.
 */
/** Solo la hora, HH:MM en Bogotá. Para la marca de tiempo de cada mensaje de chat. */
export function horaCorta(valor: string | null | undefined): string {
  if (!valor) return ''
  return new Date(valor).toLocaleTimeString('es-CO', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Bogota',
  })
}

export function horas(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return '—'

  const texto = Number.isInteger(valor)
    ? String(valor)
    : valor.toFixed(2).replace(/0$/, '').replace('.', ',')

  return `${texto} h`
}

/**
 * Un porcentaje, con coma decimal y sin decimales de más.
 *
 * "93,8 %" y no "93.8 %": el punto decimal es de otro país, y en una cifra
 * que se lee de reojo un separador equivocado se lee como miles.
 */
export function porcentaje(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return '—'

  return `${valor.toLocaleString('es-CO', { maximumFractionDigits: 1 })} %`
}

/**
 * Retraso en horas, con signo y en palabras.
 * El signo importa: adelantarse y atrasarse no son lo mismo.
 */
export function retraso(valorHoras: number | null | undefined): string {
  if (valorHoras === null || valorHoras === undefined) return '—'
  if (Math.abs(valorHoras) < 0.01) return 'A tiempo'

  return valorHoras > 0
    ? `${horas(valorHoras)} tarde`
    : `${horas(Math.abs(valorHoras))} antes`
}

/** Texto para búsquedas: minúsculas y sin tildes, igual que fn_normalizar en la base. */
export function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    // Marcas diacríticas combinantes, escritas con escapes para que el archivo
    // sea legible en cualquier editor.
    .replace(/[̀-ͯ]/g, '')
    .trim()
}

/**
 * Iniciales para el avatar.
 *
 * Se descartan los trozos que no empiezan por letra. Sin eso, «Brallan
 * (prueba)» daba «B(» y «MEGA S.A.S.» habría dado «MS» tomando el punto: el
 * avatar es lo único que identifica a la persona en la barra, y con un signo
 * de puntuación adentro se lee como un error de la aplicación.
 */
export function iniciales(nombre: string): string {
  return nombre
    .trim()
    .split(/\s+/)
    .filter((parte) => /^\p{L}/u.test(parte))
    .slice(0, 2)
    .map((parte) => parte.charAt(0).toUpperCase())
    .join('')
}
